import React, { useState, useEffect } from 'react';
import {
  Button,
  Highlight,
  HStack,
  IconButton,
  IconBannersWarning,
  Tooltip,
  Text,
  VStack,
} from '@adc/parallax-component-library';
import { IconQuestionmark } from '@adc/parallax-icons';
import i18n from '../../utilities/i18n';
import { Dialog, useMedia } from 'tamagui';
import './../modal/Modal.scss';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { availableDevicesQueryOptions } from '../../queryOptions';
import DynamicTable, {
  CheckboxFilterSection,
  ColumnConfig,
} from '../table/DynamicTable';
import { DeviceAssignmentConfirmationModal } from './DeviceAssignmentConfirmationModal';
import { attributeDeviceMutationOptions } from '../../queryOptions/device';
import {
  useDeviceAssignmentRecords,
  DeviceAssignmentRecord,
} from './hooks/useDeviceAssignmentRecords';
import { DeviceEntityType } from '../../models';
import { EntityType } from 'adc-sirius-module-common';

type Props = {
  trigger: React.ReactNode;
  entityType: DeviceEntityType;
  entityKey: string;
  entityName: string;
};

/**
 * A modal component for updating device assignments at the study, site, or subject level.
 * Handles rendering of a confirmation modal for assignment changes.
 * @param {React.ReactElement} trigger - element that opens the modal when clicked
 * @param {DeviceEntityType} entityType - the type of entity for which devices are being assigned
 * @param {string} entityKey - the unique key of the entity for which devices are being assigned
 * @param {string} entityName - the display name of the entity for which devices are being assigned, used in the confirmation modal
 * @returns {React.JSX.Element}
 */
export const AssignDeviceModal = ({
  trigger,
  entityType,
  entityKey,
  entityName,
}: Props) => {
  const media = useMedia();
  const queryClient = useQueryClient();
  const entityScopeByType: Record<
    DeviceEntityType,
    'study' | 'site' | 'subject'
  > = {
    [EntityType.Study]: 'study',
    [EntityType.StudySite]: 'site',
    [EntityType.StudySubject]: 'subject',
  };
  const entityScope = entityScopeByType[entityType];

  const [isOpen, setIsOpen] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const [defaultDeviceKeys, setDefaultDeviceKeys] = useState<string[]>([]);

  const [selectedDeviceKeys, setSelectedDeviceKeys] = useState<string[]>([]);
  const [devicesToBeAssigned, setDevicesToBeAssigned] = useState<
    DeviceAssignmentRecord[]
  >([]);
  const [devicesToBeUnassigned, setDevicesToBeUnassigned] = useState<
    DeviceAssignmentRecord[]
  >([]);

  const [filteredData, setFilteredData] = useState<DeviceAssignmentRecord[]>(
    [],
  );

  const [checkboxFilters, setCheckboxFilters] = useState<
    CheckboxFilterSection[]
  >([]);

  const devices = useQuery(availableDevicesQueryOptions({}));

  const attributeDeviceMutation = useMutation(
    attributeDeviceMutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['devices'],
        });
        queryClient.invalidateQueries({
          queryKey: ['availableDevices'],
        });
      },
    }),
  );

  const { data: rowData, filters: initialCheckboxFilters } =
    useDeviceAssignmentRecords({ entityType, entityKey });

  useEffect(() => {
    setCheckboxFilters(initialCheckboxFilters);
  }, [initialCheckboxFilters]);

  useEffect(() => {
    let keys: string[] = [];

    if (devices.data?.length) {
      keys = devices.data
        .filter((device) => device[`${entityScope}Key`] === entityKey)
        .map((device) => device.key);
    }

    setDefaultDeviceKeys(keys);
    setSelectedDeviceKeys(keys);
  }, [devices.data, setDefaultDeviceKeys, entityScope, entityKey]);

  useEffect(() => {
    let filteredDataResult = [...rowData];

    checkboxFilters.forEach((filter) => {
      const activeFilterValues = filter.items
        .filter((item) => item.value)
        .map((item) => item.id);

      if (activeFilterValues.length > 0) {
        filteredDataResult = filteredDataResult.filter((dataItem) =>
          activeFilterValues.includes(
            String(dataItem[filter.id as keyof DeviceAssignmentRecord]),
          ),
        );
      }
    });

    setFilteredData(filteredDataResult);
  }, [checkboxFilters, rowData]);

  const submitAssignments = () => {
    setDevicesToBeAssigned(
      rowData.filter(
        ({ key }) =>
          selectedDeviceKeys.includes(key) && !defaultDeviceKeys.includes(key),
      ),
    );
    setDevicesToBeUnassigned(
      rowData.filter(
        ({ key }) =>
          !selectedDeviceKeys.includes(key) && defaultDeviceKeys.includes(key),
      ),
    );

    if (
      selectedDeviceKeys.length !== defaultDeviceKeys.length ||
      !selectedDeviceKeys.every((key) => defaultDeviceKeys.includes(key))
    ) {
      setShowConfirmation(true);
    } else {
      resetState();
    }
  };

  const onSave = async () =>
    attributeDeviceMutation
      .mutateAsync({
        assignedKeys: devicesToBeAssigned.map((device) => device.key),
        unassignedKeys: devicesToBeUnassigned.map((device) => device.key),
        entityType,
        entityKey,
      })
      .then(() => {
        resetState();
      })
      .catch(() => {
        setShowConfirmation(false);
        setHasError(true);
      });

  const resetState = () => {
    setShowConfirmation(false);
    setIsOpen(false);
    setSelectedDeviceKeys(defaultDeviceKeys);
    setHasError(false);
  };

  const onSelectionChange = (keys: string[]) => {
    if (entityType === EntityType.StudySubject) {
      setSelectedDeviceKeys(
        keys.filter((key) => !selectedDeviceKeys.includes(key)),
      );
    } else {
      setSelectedDeviceKeys(keys);
    }
  };

  const columnData: ColumnConfig[] = [
    { label: i18n.t('Modal_AssignDevice.deviceName'), dataKey: 'appName' },
    { label: i18n.t('Device.model'), dataKey: 'phoneModel' },
    { label: i18n.t('Modal_AssignDevice.assignedStudy'), dataKey: 'studyName' },
    { label: i18n.t('Modal_AssignDevice.assignedSite'), dataKey: 'siteName' },
  ];

  return (
    <>
      <Dialog open={isOpen}>
        <Dialog.Trigger onPress={() => setIsOpen(true)} asChild>
          {trigger}
        </Dialog.Trigger>
        <Dialog.Portal>
          <Dialog.Overlay key="overlay" />
          <Dialog.Content key="content" width="90%">
            <Dialog.Title>
              <HStack alignItems="center" gap="$2">
                <Text type="$h3.strong">
                  {i18n.t('Modal_AssignDevice.title')}
                </Text>
                {media.sm ? (
                  <Tooltip
                    placement="bottom"
                    text={i18n.t(
                      `Modal_AssignDevice.description.${entityScope}`,
                    )}
                  >
                    <IconButton
                      size="S"
                      variant="tertiary"
                      onPress={() => {}}
                      aria-label={i18n.t('Common.moreInfo')}
                    >
                      <IconQuestionmark color="$support.neutral.default" />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </HStack>
            </Dialog.Title>

            <VStack
              flex={1}
              minHeight={0}
              gap="$2"
              paddingTop="$2"
              overflow="hidden"
            >
              {hasError ? (
                <VStack gap="$2" flexShrink={0}>
                  <Highlight
                    variant="danger"
                    iconStart={
                      <IconBannersWarning
                        color="$neutral.0"
                        accentColor="$support.danger.default"
                      />
                    }
                    title={i18n.t('Modal_AssignDevice.error.title')}
                    body={i18n.t('Modal_AssignDevice.error.description')}
                    testID="error-banner"
                  />
                </VStack>
              ) : null}

              {!media.sm ? (
                <Text>
                  {i18n.t(`Modal_AssignDevice.description.${entityScope}`)}
                </Text>
              ) : null}

              <VStack flex={1} minHeight={0} overflow="hidden">
                <DynamicTable
                  isLoading={devices.isPending}
                  hasLoadingError={devices.isError}
                  description={i18n.t('Modal_AssignDevice.tableDescription')}
                  columnData={columnData}
                  rowData={filteredData}
                  enableSelection={true}
                  enableSelectAll={entityType !== EntityType.StudySubject}
                  selectedRowKeys={selectedDeviceKeys}
                  onSelectionChange={onSelectionChange}
                  rowKeyField="key"
                  showCard={false}
                  checkboxFilters={checkboxFilters}
                  enableFiltering={true}
                  onFiltersChange={setCheckboxFilters}
                  onFiltersReset={() => {
                    setCheckboxFilters((prevFilters) =>
                      prevFilters.map((filter) => ({
                        ...filter,
                        items: filter.items.map((item) => ({
                          ...item,
                          value: false,
                        })),
                      })),
                    );
                  }}
                />
              </VStack>
            </VStack>

            <HStack
              alignSelf="flex-end"
              gap="$2"
              flexShrink={0}
              paddingTop="$1"
            >
              <Button
                variant="secondary"
                onPress={resetState}
                label={i18n.t('Common.cancel')}
                testID="cancel"
              />
              <Button
                variant="primary"
                onPress={submitAssignments}
                label={i18n.t('Common.save')}
                testID="save"
              />
            </HStack>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog>
      <DeviceAssignmentConfirmationModal
        entityName={entityName}
        entityType={entityType}
        unassignedDevices={devicesToBeUnassigned}
        assignedDevices={devicesToBeAssigned}
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onSave={onSave}
      />
    </>
  );
};
